import { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Products from './pages/Products';
import Invoices from './pages/Invoices';
import Returns from './pages/Returns';
import Quotations from './pages/Quotations';
import CashRegister from './pages/CashRegister';
import PendingPaymentInvoices from './pages/PendingPaymentInvoices';
import BusinessConfig from './pages/BusinessConfig';
import Suppliers from './pages/Suppliers';
import AccountsPayable from './pages/AccountsPayable';
import AccountsReceivable from './pages/AccountsReceivable';
import EstadoCuenta from './pages/EstadoCuenta';
import Reports from './pages/Reports';
import InventoryReport from './pages/InventoryReport';
import NCFManagement from './pages/NCFManagement';
import ComprobantesFiscales from './pages/ComprobantesFiscales';
import Certificados from './pages/Certificados';
import Report607 from './pages/Report607';
import Customers from './pages/Customers';
import POS from './pages/POS';
import Categories from './pages/Categories';
import SystemReset from './pages/SystemReset';
import SystemParameters from './pages/SystemParameters';
import SystemLogs from './pages/SystemLogs';
import ChangePassword from './pages/ChangePassword';
import Purchases from './pages/Purchases';
import InventoryAdjustments from './pages/InventoryAdjustments';
import PriceChanges from './pages/PriceChanges';
import LabelPrinting from './pages/LabelPrinting';
import Ofertas from './pages/Ofertas';
import PriceLists from './pages/PriceLists';
import Layout from './components/Layout';
import { Toaster } from './components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Axios interceptor to add token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Axios response interceptor to handle session invalidation
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && error.response?.data?.detail === 'session_invalidated') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login?session_expired=1';
    }
    // Sistema suspendido: forzar logout y redirigir al login con flag
    const detail = error.response?.data?.detail;
    if (error.response?.status === 403 && detail && typeof detail === 'object' && detail.code === 'system_suspended') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // No redirigir si ya estamos en /login (evita bucles)
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login?suspended=1';
      }
    }
    return Promise.reject(error);
  }
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/login" replace />;
};

function App() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  // Actualizar título de la página dinámicamente
  useEffect(() => {
    const updatePageTitle = async () => {
      try {
        const response = await axios.get(`${API}/business-config-public`);
        const config = response.data;
        
        // Actualizar título: "Nombre del Sistema - Slogan"
        const title = `${config.nombre_comercio || config.sistema_nombre || 'Nombre Comercial *'} - ${config.slogan || 'Sistema de Gestión'}`;
        document.title = title;
      } catch (error) {
        console.error('Error al cargar configuración para título:', error);
        // Mantener título por defecto si falla
        document.title = 'Sistema de Gestión';
      }
    };

    updatePageTitle();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login setUser={setUser} />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <Dashboard user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <Users user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/products"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <Products user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/price-lists"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <PriceLists user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <Invoices user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/devoluciones"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <Returns user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotations"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <Quotations user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cash-register"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <CashRegister user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cobro-facturas"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <PendingPaymentInvoices user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/config"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <BusinessConfig user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ncf-management"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <NCFManagement user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/comprobantes-fiscales"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <ComprobantesFiscales user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/certificados"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  {(user || JSON.parse(localStorage.getItem('user') || 'null'))?.role === 'superadmin' ? <Certificados /> : <Navigate to="/" replace />}
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/suppliers"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <Suppliers user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchases"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <Purchases user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts-payable"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <AccountsPayable user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts-receivable"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <AccountsReceivable user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/estado-cuenta"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <EstadoCuenta user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <Reports user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/607"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <Report607 />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/inventory"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <InventoryReport user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <Customers user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pos"
            element={
              <ProtectedRoute>
                <POS user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/categories"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <Categories user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-reset"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <SystemReset user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/parameters"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <SystemParameters user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/change-password"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <ChangePassword user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory-adjustments"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <InventoryAdjustments user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/price-changes"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <PriceChanges user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/label-printing"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <LabelPrinting user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ofertas"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <Ofertas user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-logs"
            element={
              <ProtectedRoute>
                <Layout user={user} setUser={setUser}>
                  <SystemLogs user={user} />
                </Layout>
              </ProtectedRoute>
            }
          />

        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;
